import styled from 'styled-components';
import { buildTradeDashboardColorCss } from '../../constants/tradeTheme';

export const ServiceDashboardWrapper = styled.div`
  ${buildTradeDashboardColorCss()}

  .dashboard-page {
    display: flex;
    flex-direction: column;
    background-color: #ffffff;
  }

  /* ── Trust banner ────────────────────────────────────────────────────────── */

  .trust-banner {
    background: linear-gradient(135deg, #eef2ff 0%, #f0f7ff 100%);
    border-bottom: 1px solid rgba(13, 78, 254, 0.08);
    padding: 10px 40px;
    @media (max-width: 960px) { padding: 10px 20px; }
  }

  .trust-banner-inner {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 24px;
    max-width: 1200px;
    margin: 0 auto;
    flex-wrap: wrap;
    @media (max-width: 600px) { flex-direction: column; gap: 4px; }
  }

  .trust-item {
    display: flex;
    align-items: center;
    gap: 5px;
    color: #5a6478;
    font-size: 0.8rem;
    font-weight: 500;
    white-space: nowrap;
    svg { color: #0d4efe; opacity: 0.7; }
  }

  .trust-divider {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.15);
    @media (max-width: 600px) { display: none; }
  }

  /* ── Content area ────────────────────────────────────────────────────────── */

  .content-area {
    flex: 1;
    padding: 28px 40px 40px;
    max-width: 960px;
    width: 100%;
    margin: 0 auto;
    @media (max-width: 960px) { padding: 20px; }
  }

  /* ── Loading & error states ──────────────────────────────────────────────── */

  .loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 80px 20px;
    color: #5a6478;
  }

  .error-banner {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    background: rgba(220, 53, 69, 0.06);
    border: 1px solid rgba(220, 53, 69, 0.15);
    border-radius: 10px;
    margin-bottom: 20px;
    font-size: 0.84rem;
    color: #b91c1c;
    line-height: 1.4;

    svg { color: #dc3545; flex-shrink: 0; }

    span { flex: 1; }
  }

  .error-dismiss {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    min-width: 28px;
    border-radius: 6px;
    border: none;
    background: transparent;
    color: #dc3545;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 0.12s ease;

    &:hover {
      background: rgba(220, 53, 69, 0.08);
    }
  }

  /* ── Page header ─────────────────────────────────────────────────────────── */

  .page-header { margin-bottom: 24px; }

  .page-title-row {
    display: flex;
    align-items: flex-start;
    gap: 14px;
  }

  .page-icon {
    color: #0d4efe;
    opacity: 0.8;
    margin-top: 2px;
  }

  .page-title {
    font-weight: 900 !important;
    letter-spacing: -0.4px;
    color: #1a1a2e;
    line-height: 1.2 !important;
  }

  .page-subtitle {
    color: #4a5568;
    line-height: 1.4 !important;
    margin-top: 2px !important;
  }

  /* ── Stats strip ─────────────────────────────────────────────────────────── */

  .stats-strip {
    display: flex;
    align-items: center;
    gap: 0;
    background: linear-gradient(135deg, #f0f4ff 0%, #f5f8ff 100%);
    border: 1.5px solid rgba(13, 78, 254, 0.1);
    border-radius: 14px;
    padding: 18px 0;
    margin-bottom: 28px;

    @media (max-width: 600px) {
      flex-direction: column;
      gap: 12px;
      padding: 16px 20px;
    }
  }

  .stat-card {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
  }

  .stat-number {
    font-size: 1.6rem;
    font-weight: 800;
    color: #0d4efe;
    line-height: 1;
  }

  .stat-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #6b7a94;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  .stat-divider {
    width: 1px;
    height: 32px;
    background: rgba(0, 0, 0, 0.08);
    @media (max-width: 600px) { width: 60px; height: 1px; }
  }

  /* ── Sections ────────────────────────────────────────────────────────────── */

  .section { margin-bottom: 32px; }

  .section-label {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
    color: #3a4560;
    svg { color: #0d4efe; opacity: 0.85; }
    .MuiTypography-root {
      font-weight: 700 !important;
      letter-spacing: 0.5px;
      color: #3a4560;
    }
  }

  .info-banner {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 16px;
    background: rgba(13, 78, 254, 0.05);
    border: 1px solid rgba(13, 78, 254, 0.14);
    border-radius: 10px;
    margin-bottom: 14px;
    font-size: 0.84rem;
    color: #4a5568;
    line-height: 1.4;
    svg { color: #0d4efe; opacity: 0.75; flex-shrink: 0; margin-top: 1px; }
  }

  .search-bar {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border-radius: 10px;
    border: 1.5px solid rgba(0, 0, 0, 0.08);
    background: #ffffff;
    margin-bottom: 14px;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
    &:focus-within {
      border-color: #0d4efe;
      box-shadow: 0 0 0 3px rgba(13, 78, 254, 0.08);
    }
    svg { color: #8a94a6; flex-shrink: 0; }
    input {
      flex: 1;
      border: none;
      outline: none;
      font-family: inherit;
      font-size: 0.875rem;
      color: #1a1a2e;
      background: transparent;
      &::placeholder { color: #aab3c0; }
    }
  }

  .trade-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .empty-state {
    text-align: center;
    padding: 32px 16px;
    font-size: 0.9rem;
    color: #8a94a6;
    font-weight: 500;
  }

  /* ── Trade color classes (from tradeTheme.js) ────────────────────────────── */

  /* ── Trade card ──────────────────────────────────────────────────────────── */

  .trade-card {
    border: 1.5px solid rgba(0, 0, 0, 0.09);
    border-radius: 14px;
    background: #ffffff;
    overflow: hidden;
    transition: border-color 0.15s ease, box-shadow 0.18s ease;
    &:hover {
      border-color: rgba(0, 0, 0, 0.15);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    }
    &.enrolled { border-color: rgba(13, 78, 254, 0.18); background: #fcfdff; }
  }

  .trade-header {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px 20px;
    cursor: pointer;
    user-select: none;
    transition: background 0.12s ease;
    &:hover { background: rgba(0, 0, 0, 0.015); }
    @media (max-width: 600px) { padding: 14px 14px; gap: 10px; }
  }

  .available-header {
    cursor: default;
    &:hover { background: transparent; }
  }

  .trade-icon-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    min-width: 40px;
    border-radius: 10px;
    transition: background 0.15s ease;
  }

  .trade-info { flex: 1; min-width: 0; }

  .trade-name {
    font-weight: 700 !important;
    color: #1a1a2e;
    line-height: 1.3 !important;
  }

  .trade-meta {
    font-size: 0.78rem;
    color: #6b7a94;
    font-weight: 500;
  }

  .trade-header-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }

  .enrollment-badge {
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    padding: 4px 10px;
    border-radius: 6px;
    &.active { background: rgba(5, 150, 105, 0.12); color: #047857; }
    &.browsing { background: rgba(245, 158, 11, 0.12); color: #d97706; }
    @media (max-width: 600px) { display: none; }
  }

  .expand-icon {
    display: flex;
    align-items: center;
    color: #8a94a6;
    transition: color 0.15s ease;
  }

  .enroll-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 18px;
    border-radius: 10px;
    border: 1.5px solid rgba(13, 78, 254, 0.2);
    background: rgba(13, 78, 254, 0.03);
    color: #0d4efe;
    font-family: inherit;
    font-size: 0.85rem;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, transform 0.1s ease;
    flex-shrink: 0;
    &:hover {
      background: rgba(13, 78, 254, 0.08);
      border-color: rgba(13, 78, 254, 0.35);
      box-shadow: 0 2px 8px rgba(13, 78, 254, 0.1);
    }
    &:active { transform: scale(0.97); }
  }

  /* ── Available trade preview chips ───────────────────────────────────────── */

  .available-preview {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 0 20px 16px;
    @media (max-width: 600px) { padding: 0 14px 14px; }
  }

  .preview-chip {
    font-size: 0.72rem;
    font-weight: 600;
    color: #8a94a6;
    background: #f3f5f8;
    padding: 4px 10px;
    border-radius: 6px;
    white-space: nowrap;
    &.more { background: transparent; color: #0d4efe; font-weight: 700; }
  }

  /* ── Trade body (expanded subservices) ───────────────────────────────────── */

  .trade-body {
    border-top: 1px solid rgba(0, 0, 0, 0.05);
    padding: 0;
  }

  .subservice-controls {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 20px;
    background: #f0f4fa;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    @media (max-width: 600px) { padding: 10px 14px; }
  }

  .bulk-toggle-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 12px;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    background: #ffffff;
    color: #5a6478;
    font-family: inherit;
    font-size: 0.78rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;
    &:hover { border-color: rgba(0, 0, 0, 0.15); color: #1a1a2e; background: #f7fafc; }
  }

  .unenroll-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 12px;
    border-radius: 8px;
    border: 1px solid rgba(220, 53, 69, 0.12);
    background: transparent;
    color: #8a94a6;
    font-family: inherit;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;
    &:hover {
      border-color: rgba(220, 53, 69, 0.3);
      color: #dc3545;
      background: rgba(220, 53, 69, 0.03);
    }
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  /* ── Subservice items ────────────────────────────────────────────────────── */

  .subservice-list {
    display: flex;
    flex-direction: column;
  }

  .service-group-accordion {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    &:last-child {
      border-bottom: none;
    }
  }

  .subservice-group-accordion-summary.MuiAccordionSummary-root {
    padding-left: 20px;
    padding-right: 12px;
    min-height: unset;
    background:transparent;
    @media (max-width: 600px) {
      padding-left: 14px;
      padding-right: 10px;
    }
    &:hover {
      background: rgba(0, 0, 0, 0.02);
    }
  }

  .subservice-group-header {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 2px 0 4px;
    flex: 1;
    min-width: 0;
    background: transparent;
  }

  .subservice-group-icon {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 2px;
  }

  .subservice-group-text {
    flex: 1;
    min-width: 0;
  }

  .subservice-group-name-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }

  .subservice-group-name {
    font-size: 0.8rem;
    font-weight: 700;
    color: #1a1a2e;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }

  .subservice-group-badge {
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #0d4efe;
    background: rgba(13, 78, 254, 0.1);
    padding: 2px 8px;
    border-radius: 999px;
  }

  .subservice-group-desc {
    font-size: 0.75rem;
    color: #6b7280;
    line-height: 1.45;
    margin-top: 4px;
  }

  .subservice-item {
    border-bottom: 1px solid rgba(0, 0, 0, 0.04);
    &:last-child { border-bottom: none; }

    &.enabled {
      background: rgba(13, 78, 254, 0.03);
      border-left: 3px solid rgba(13, 78, 254, 0.3);
    }

    &.urgent .subservice-icon {
      background: rgba(220, 53, 69, 0.08) !important;
      color: #dc3545 !important;
    }
  }

  .subservice-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 20px;
    cursor: pointer;
    user-select: none;
    transition: background 0.1s ease;

    &:hover { background: rgba(0, 0, 0, 0.015); }

    .subservice-item.enabled &:hover {
      background: rgba(13, 78, 254, 0.03);
    }

    @media (max-width: 600px) {
      padding: 12px 14px;
      gap: 8px;
      flex-wrap: wrap;
    }
  }

  .subservice-toggle {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    .toggle-on { color: #0d4efe; }
    .toggle-off { color: #c8cdd6; }
  }

  /* trade-specific toggle colors generated in buildTradeDashboardColorCss() */

  .subservice-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    min-width: 28px;
    border-radius: 7px;
    flex-shrink: 0;
  }

  .subservice-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  .subservice-label {
    font-size: 0.84rem;
    font-weight: 600;
    color: #1a1a2e;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .urgent-tag {
    font-size: 0.65rem;
    font-weight: 800;
    color: #dc3545;
    background: rgba(220, 53, 69, 0.08);
    padding: 1px 6px;
    border-radius: 4px;
    letter-spacing: 0.3px;
    text-transform: uppercase;
  }

  .subservice-desc {
    font-size: 0.76rem;
    color: #6b7a94;
    line-height: 1.35;
    font-weight: 400;
    @media (max-width: 600px) { display: none; }
  }

  .config-summary {
    font-size: 0.72rem;
    color: #0b43d4;
    font-weight: 600;
    margin-top: 2px;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  /* ── Config toggle button ────────────────────────────────────────────────── */

  .config-toggle-btn {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 4px 8px;
    border-radius: 6px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    background: #ffffff;
    color: #8a94a6;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;

    &:hover {
      border-color: rgba(13, 78, 254, 0.25);
      color: #0d4efe;
      background: rgba(13, 78, 254, 0.03);
    }
    &.open {
      border-color: rgba(13, 78, 254, 0.2);
      color: #0d4efe;
      background: rgba(13, 78, 254, 0.04);
    }
  }

  /* ── Config panel (inline — kept for editing already-enabled services) ──── */

  .config-panel {
    padding: 14px 20px 18px 72px;
    background: #f8fafb;
    border-top: 1px solid rgba(0, 0, 0, 0.04);
    display: flex;
    flex-direction: column;
    gap: 16px;

    @media (max-width: 600px) {
      padding: 14px 14px 18px 14px;
    }
  }

  .config-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .config-section-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.76rem;
    font-weight: 700;
    color: #5a6478;
    text-transform: uppercase;
    letter-spacing: 0.3px;

    svg { color: #8a94a6; }
  }

  .config-options-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .config-pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 6px 14px;
    border-radius: 8px;
    border: 1.5px solid rgba(0, 0, 0, 0.08);
    background: #ffffff;
    color: #5a6478;
    font-family: inherit;
    font-size: 0.78rem;
    font-weight: 600;
    cursor: pointer;
    transition: border-color 0.12s ease, background 0.12s ease, color 0.12s ease, box-shadow 0.12s ease;

    &:hover {
      border-color: rgba(13, 78, 254, 0.2);
      background: rgba(13, 78, 254, 0.02);
    }

    &.active {
      border-color: #0d4efe;
      background: rgba(13, 78, 254, 0.06);
      color: #0d4efe;
      font-weight: 700;
      box-shadow: 0 1px 4px rgba(13, 78, 254, 0.1);
    }
  }

  .contact-pill {
    svg { flex-shrink: 0; }

    &.active svg { color: #0d4efe; }
  }

  /* ── Price inputs ────────────────────────────────────────────────────────── */

  .config-price-inputs {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 4px;
  }

  .price-input-group {
    display: flex;
    align-items: center;
    gap: 0;
    border: 1.5px solid rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    overflow: hidden;
    background: #ffffff;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus-within {
      border-color: #0d4efe;
      box-shadow: 0 0 0 3px rgba(13, 78, 254, 0.08);
    }
  }

  .price-prefix {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 8px 0 10px;
    font-size: 0.84rem;
    font-weight: 700;
    color: #8a94a6;
    background: #f7fafc;
    border-right: 1px solid rgba(0, 0, 0, 0.06);
    height: 34px;
  }

  .price-input-group input {
    border: none;
    outline: none;
    padding: 7px 10px;
    font-family: inherit;
    font-size: 0.84rem;
    font-weight: 600;
    color: #1a1a2e;
    width: 72px;
    background: transparent;

    &::placeholder { color: #aab3c0; font-weight: 500; }
  }

  .price-separator {
    font-size: 0.9rem;
    font-weight: 600;
    color: #8a94a6;
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     SERVICE CONFIG MODAL — Required before enabling a subservice
     ═══════════════════════════════════════════════════════════════════════════ */

  .config-modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(15, 17, 30, 0.45);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    animation: overlayFadeIn 0.2s ease;
    padding: 20px;
  }

  @keyframes overlayFadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes modalSlideUp {
    from { opacity: 0; transform: translateY(16px) scale(0.97); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }

  .config-modal {
    background: #ffffff;
    border-radius: 18px;
    width: 100%;
    max-width: 520px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow:
      0 24px 80px rgba(0, 0, 0, 0.15),
      0 4px 20px rgba(0, 0, 0, 0.06);
    animation: modalSlideUp 0.25s ease;
    display: flex;
    flex-direction: column;

    &::-webkit-scrollbar { width: 6px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.1);
      border-radius: 3px;
    }
  }

  /* ── Modal header ────────────────────────────────────────────────────────── */

  .config-modal-header {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 24px 24px 0;
    position: sticky;
    top: 0;
    background: #ffffff;
    z-index: 1;
    border-radius: 18px 18px 0 0;
  }

  .config-modal-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    min-width: 44px;
    border-radius: 12px;
    flex-shrink: 0;
  }

  .config-modal-title-area {
    flex: 1;
    min-width: 0;
  }

  .config-modal-title {
    font-weight: 800 !important;
    color: #1a1a2e;
    line-height: 1.25 !important;
    letter-spacing: -0.2px;
  }

  .config-modal-subtitle {
    font-size: 0.82rem;
    color: #8a94a6;
    font-weight: 500;
    line-height: 1.35;
    margin-top: 2px;
  }

  .config-modal-close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    min-width: 32px;
    border-radius: 8px;
    border: none;
    background: transparent;
    color: #8a94a6;
    cursor: pointer;
    transition: background 0.12s ease, color 0.12s ease;
    flex-shrink: 0;
    margin-top: 2px;

    &:hover {
      background: rgba(0, 0, 0, 0.05);
      color: #1a1a2e;
    }
  }

  /* ── Modal step indicator ────────────────────────────────────────────────── */

  .config-modal-steps {
    display: flex;
    align-items: center;
    gap: 0;
    padding: 20px 24px 0;
  }

  .config-step-item {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
  }

  .config-step-number {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    min-width: 26px;
    border-radius: 50%;
    font-size: 0.72rem;
    font-weight: 800;
    transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;

    &.pending {
      background: #f0f2f5;
      color: #8a94a6;
    }
    &.active {
      background: #0d4efe;
      color: #ffffff;
      box-shadow: 0 2px 8px rgba(13, 78, 254, 0.25);
    }
    &.complete {
      background: rgba(1, 235, 178, 0.12);
      color: #059669;
    }
  }

  .config-step-label {
    font-size: 0.74rem;
    font-weight: 600;
    color: #8a94a6;
    white-space: nowrap;

    &.active { color: #1a1a2e; font-weight: 700; }
    &.complete { color: #059669; }
  }

  .config-step-connector {
    flex: 0 0 auto;
    width: 32px;
    height: 2px;
    margin: 0 6px;
    border-radius: 1px;
    background: #e8ebf0;
    transition: background 0.2s ease;

    &.complete { background: rgba(1, 235, 178, 0.4); }
  }

  /* ── Modal body ──────────────────────────────────────────────────────────── */

  .config-modal-body {
    padding: 20px 24px;
    display: flex;
    flex-direction: column;
    gap: 22px;
  }

  .modal-config-section {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .modal-config-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.8rem;
    font-weight: 700;
    color: #3a4050;
    letter-spacing: 0.2px;

    svg { color: #0d4efe; opacity: 0.65; flex-shrink: 0; }
  }

  .modal-config-hint {
    font-size: 0.74rem;
    color: #8a94a6;
    font-weight: 400;
    line-height: 1.4;
    margin-top: -4px;
  }

  /* ── Modal pricing: Price type selector (2×2 grid) ───────────────────────── */

  .modal-price-type-options {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;

    @media (max-width: 400px) {
      grid-template-columns: 1fr;
    }
  }

  .modal-price-type-pill {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    padding: 12px 14px;
    border-radius: 10px;
    border: 1.5px solid rgba(0, 0, 0, 0.08);
    background: #ffffff;
    cursor: pointer;
    font-family: inherit;
    text-align: left;
    transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      border-color: rgba(13, 78, 254, 0.2);
      background: rgba(13, 78, 254, 0.02);
    }

    &.active {
      border-color: #0d4efe;
      background: rgba(13, 78, 254, 0.05);
      box-shadow: 0 2px 8px rgba(13, 78, 254, 0.08);
    }

    .pill-label {
      font-size: 0.82rem;
      font-weight: 700;
      color: #1a1a2e;
      line-height: 1.3;
    }

    .pill-hint {
      font-size: 0.7rem;
      font-weight: 400;
      color: #8a94a6;
      line-height: 1.3;
    }

    &.active .pill-label {
      color: #0d4efe;
    }

    &.active .pill-hint {
      color: #6b8cfe;
    }
  }

  /* ── Modal pricing: Range & fixed inputs ─────────────────────────────────── */

  .modal-price-row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    margin-top: 4px;
  }

  .modal-price-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    min-width: 120px;
  }

  .modal-price-label {
    font-size: 0.7rem;
    font-weight: 600;
    color: #8a94a6;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  .modal-price-input-wrap {
    display: flex;
    align-items: center;
    border: 1.5px solid rgba(0, 0, 0, 0.1);
    border-radius: 10px;
    overflow: hidden;
    background: #ffffff;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus-within {
      border-color: #0d4efe;
      box-shadow: 0 0 0 3px rgba(13, 78, 254, 0.08);
    }

    &.error {
      border-color: rgba(220, 53, 69, 0.5);
      &:focus-within {
        box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.08);
      }
    }

    &.disabled {
      opacity: 0.45;
      pointer-events: none;
    }
  }

  .modal-price-prefix {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 10px;
    font-size: 0.88rem;
    font-weight: 700;
    color: #8a94a6;
    background: #f7fafc;
    border-right: 1px solid rgba(0, 0, 0, 0.06);
    height: 40px;
  }

  .modal-price-input-wrap input {
    border: none;
    outline: none;
    padding: 9px 12px;
    font-family: inherit;
    font-size: 0.88rem;
    font-weight: 600;
    color: #1a1a2e;
    width: 100%;
    background: transparent;
    &::placeholder { color: #c0c7d2; font-weight: 500; }

    /* Hide number input spinners */
    -moz-appearance: textfield;
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  .modal-price-or {
    font-size: 0.78rem;
    font-weight: 600;
    color: #aab3c0;
    text-align: center;
    flex-shrink: 0;
    padding-top: 18px;
  }

  /* ── Modal: Tagline field ────────────────────────────────────────────────── */

  .modal-tagline-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .modal-tagline-label {
    font-size: 0.76rem;
    font-weight: 600;
    color: #5a6478;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  .modal-tagline-input {
    border: 1.5px solid rgba(0, 0, 0, 0.1);
    border-radius: 10px;
    padding: 10px 14px;
    font-family: inherit;
    font-size: 0.85rem;
    font-weight: 500;
    color: #1a1a2e;
    outline: none;
    background: #ffffff;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus {
      border-color: #0d4efe;
      box-shadow: 0 0 0 3px rgba(13, 78, 254, 0.08);
    }

    &::placeholder {
      color: #b0b8c4;
      font-weight: 400;
    }
  }

  .modal-tagline-hint {
    font-size: 0.7rem;
    color: #8a94a6;
    font-weight: 400;
    line-height: 1.35;
  }

  .modal-description-input {
    border: 1.5px solid rgba(0, 0, 0, 0.1);
    border-radius: 10px;
    padding: 10px 14px;
    font-family: inherit;
    font-size: 0.85rem;
    font-weight: 500;
    color: #1a1a2e;
    outline: none;
    background: #ffffff;
    resize: vertical;
    min-height: 96px;
    width: 100%;
    box-sizing: border-box;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus {
      border-color: #0d4efe;
      box-shadow: 0 0 0 3px rgba(13, 78, 254, 0.08);
    }

    &::placeholder {
      color: #b0b8c4;
      font-weight: 400;
    }
  }

  /* ── Modal: Schedulable toggle ───────────────────────────────────────────── */

  .modal-schedulable-toggle {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    border: 1.5px solid rgba(13, 78, 254, 0.1);
    border-radius: 10px;
    background: rgba(13, 78, 254, 0.02);
    margin-top: 6px;
    cursor: pointer;
    transition: border-color 0.12s ease, background 0.12s ease;

    &:hover {
      border-color: rgba(13, 78, 254, 0.2);
    }

    &.active {
      border-color: rgba(13, 78, 254, 0.3);
      background: rgba(13, 78, 254, 0.05);
    }

    input[type="checkbox"] {
      accent-color: #0d4efe;
      width: 16px;
      height: 16px;
      flex-shrink: 0;
    }

    .schedulable-label {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 1px;
    }

    .schedulable-title {
      font-size: 0.82rem;
      font-weight: 700;
      color: #1a1a2e;
    }

    .schedulable-desc {
      font-size: 0.72rem;
      color: #8a94a6;
      font-weight: 400;
    }
  }

  /* ── Modal validation ────────────────────────────────────────────────────── */

  .modal-field-error {
    font-size: 0.72rem;
    font-weight: 600;
    color: #dc3545;
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 2px;

    svg { flex-shrink: 0; }
  }

  /* ── Modal footer ────────────────────────────────────────────────────────── */

  .config-modal-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 24px 24px;
    position: sticky;
    bottom: 0;
    background: #ffffff;
    border-top: 1px solid rgba(0, 0, 0, 0.05);
    border-radius: 0 0 18px 18px;
  }

  .config-modal-footer-left {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.74rem;
    color: #8a94a6;
    font-weight: 500;

    svg { color: #8a94a6; opacity: 0.6; }
  }

  .config-modal-footer-actions {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .modal-btn-secondary {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 10px 20px;
    border-radius: 10px;
    border: 1.5px solid rgba(0, 0, 0, 0.1);
    background: #ffffff;
    color: #5a6478;
    font-family: inherit;
    font-size: 0.84rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;

    &:hover {
      border-color: rgba(0, 0, 0, 0.18);
      color: #1a1a2e;
      background: #f7fafc;
    }
  }

  .modal-btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 24px;
    border-radius: 10px;
    border: none;
    background: #0d4efe;
    color: #ffffff;
    font-family: inherit;
    font-size: 0.84rem;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.1s ease, opacity 0.15s ease;
    box-shadow: 0 2px 8px rgba(13, 78, 254, 0.2);

    &:hover {
      background: #0b3fd4;
      box-shadow: 0 4px 16px rgba(13, 78, 254, 0.25);
    }
    &:active { transform: scale(0.97); }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      box-shadow: none;
      &:hover { background: #0d4efe; box-shadow: none; }
      &:active { transform: none; }
    }

    &.saving {
      pointer-events: none;
      opacity: 0.8;
    }

    svg { flex-shrink: 0; }
  }

  /* ── Config status on subservice row ─────────────────────────────────────── */

  .config-required-hint {
    font-size: 0.72rem;
    color: #d97706;
    font-weight: 600;
    margin-top: 2px;
    display: flex;
    align-items: center;
    gap: 4px;
    svg { flex-shrink: 0; }
  }
`;